import { useCallback, useEffect, useRef, useState } from 'react';
import { mostUrgentWarning, warningsCrossed } from './examClock';

/**
 * The warnings a student gets as the clock runs down.
 *
 * The marks come from the paper rather than a constant here, because a short
 * paper carries its own: a ten minute paper cannot warn at fifteen.
 *
 * A warning fires as the clock passes a mark, and only then. Opening a paper
 * that already has seven minutes left does not fire the fifteen and ten minute
 * warnings retroactively, and a tab that wakes having slept past several marks
 * shows only the most urgent rather than a queue of stale ones.
 */
export const useExamWarnings = (remainingSeconds: number, warnAtMinutes: number[] = []) => {
  const previousRef = useRef<number | undefined>(undefined);
  const [warning, setWarning] = useState<number | undefined>(undefined);

  const dismiss = useCallback(() => setWarning(undefined), []);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = remainingSeconds;

    // The first reading is a starting point, not a crossing.
    if (previous === undefined) {
      return;
    }

    const crossed = mostUrgentWarning(warningsCrossed(warnAtMinutes, previous, remainingSeconds));

    if (crossed !== undefined) {
      setWarning(crossed);
    }
  }, [remainingSeconds, warnAtMinutes]);

  return { warning, dismiss };
};

export default useExamWarnings;
